import { x, Button, Logo, ButtonDefaultProps } from '@cropink/ui-components';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { MarginsProps } from '@constants/types/MarginsProps.type';
import { useAppNavigate } from '@hooks';

type InternalErrorProps = {
  errorCode: string;
  description: string;
  header?: string;
  buttonLabel?: string;
  isFullscreen?: boolean;
  hasLogo?: boolean;
  onButtonClick?: ButtonDefaultProps['onClick'];
};

export const InternalError: FC<InternalErrorProps & MarginsProps> = ({
  errorCode,
  description,
  header,
  buttonLabel,
  isFullscreen = false,
  hasLogo,
  onButtonClick,
  ...rest
}) => {
  const { t } = useTranslation();
  const navigate = useAppNavigate();

  const onClick = onButtonClick || ((): void => navigate('/'));

  return (
    <x.div
      w="600px"
      mx="auto"
      h={isFullscreen ? '100vh' : '100%'}
      display="flex"
      flexDirection="column"
      justifyContent="center"
      gap="400"
      textAlign="center"
      data-testid="internal-error-component"
      {...rest}
    >
      {hasLogo && <Logo size="large" color="black" />}
      <x.div
        position="relative"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        py="200"
        pointerEvents="none"
        userSelect="none"
      >
        <x.span
          display="inline-block"
          fontSize="160px"
          lineHeight="1em"
          fontWeight="700"
          color="content-disabled"
          opacity=".4"
        >
          {errorCode}
        </x.span>
        <x.strong
          position="absolute"
          bottom="50%"
          transform="translateY(50%)"
          text="strong-300"
          zIndex="1"
        >
          {header || t('NotFound.Header')}
        </x.strong>
        <x.div
          position="absolute"
          h="50%"
          w="100%"
          backdropFilter="blur(6px)"
          bottom={0}
        >
          <x.div
            h="100%"
            w="100%"
            backgroundColor="background-default"
            opacity=".5"
          />
        </x.div>
      </x.div>
      <x.span color="content-secondary" text="medium-75">
        {description}
      </x.span>
      <x.div>
        <Button
          onClick={onClick}
          label={buttonLabel || t('NotFound.ButtonLabel')}
        />
      </x.div>
    </x.div>
  );
};
